import { Fragment, type ReactNode, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useMatch, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { StatusPill } from "../../components/manager/StatusPill";
import { useTenantAuth } from "../../context/auth";
import { apiGet, apiPatch, apiPost } from "../../lib/api";
import { cn } from "../../lib/cn";
import { allowedActionsForStatus, nextStatusFromAction, primaryActionForStatus, riskRank } from "./calendar/logic";

type Tab = "today" | "day" | "queue";
type Status = "pending_confirmation" | "confirmed" | "completed" | "cancelled" | "no_show";
type Action = "confirm" | "cancel" | "complete" | "no_show" | "reschedule";
type WaitlistStatus = "open" | "contacted" | "booked" | "closed";
type SpecialistMode = "all" | "by_service" | "specific";
type PlannerView = "agenda" | "grid";

type Appointment = {
  id: string;
  branch_id: string;
  service_id: string;
  staff_id: string;
  customer_id: string;
  start_at: string;
  end_at: string;
  status: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  service_name?: string | null;
  staff_name?: string | null;
  branch_name?: string | null;
  branch_timezone?: string | null;
  source_channel?: string | null;
  notes?: string | null;
  customer_risk_tier?: string | null;
  allowed_actions?: string[];
  updated_at?: string;
};

type WaitlistItem = {
  id: string;
  status: WaitlistStatus;
  desired_day: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  service_name?: string | null;
  preferred_staff_name?: string | null;
  notes?: string | null;
};

type TimelineItem = { id: string; event_type: string; created_at: string; payload_json?: Record<string, unknown> };
type Slot = { staff_id: string; start_at: string; end_at: string; score?: number };
type ListResp = { items: Appointment[] };
type LayoutInterval = { id?: string; start_at: string; end_at: string; reason?: string | null; label?: string | null };
type StaffDayLayout = { staff_id: string; staff_name?: string | null; working_windows: LayoutInterval[]; time_off: LayoutInterval[] };
type DayLayoutResp = { items: StaffDayLayout[]; meta?: { day?: string; branch_timezone?: string; day_start_at?: string; day_end_at?: string } };
type QuickCreateDrawerState =
  | { open: false }
  | { open: true; day: string; minuteHint?: number; staff_id?: string };
type SimpleBranch = { id: string; name: string };
type CustomerOption = { id: string; full_name: string; phone?: string | null; branch_id?: string | null };
type StaffOptionRow = { id: string; full_name: string; branch_id?: string | null; is_active?: boolean };
type ServiceOptionRow = { id: string; name: string; duration_minutes: number; branch_id?: string | null; is_active?: boolean };

type ActionDialog =
  | { open: false }
  | { open: true; appointment: Appointment; action: Exclude<Action, "reschedule"> };
type RescheduleDialog =
  | { open: false }
  | { open: true; appointment: Appointment; day: string; staff_id: string };

const STATUSS: Status[] = ["pending_confirmation", "confirmed", "completed", "cancelled", "no_show"];
const HALF_HOUR = 30;
const DEFAULT_GRID_START_HOUR = 9;
const DEFAULT_GRID_END_HOUR = 21;

function roundDown(mins: number, step = HALF_HOUR) {
  return Math.floor(mins / step) * step;
}
function roundUp(mins: number, step = HALF_HOUR) {
  return Math.ceil(mins / step) * step;
}
function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}
function minutesSinceMidnight(d: Date) {
  return d.getHours() * 60 + d.getMinutes();
}
function minuteLabel(mins: number, locale: string) {
  const d = new Date();
  d.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
  return d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
}
function serviceKeyFor(a: Partial<Appointment>) {
  if (a.service_id) return `id:${a.service_id}`;
  return `name:${String(a.service_name || "").trim().toLowerCase()}`;
}
function serviceLabelFor(a: Partial<Appointment>) {
  return a.service_name || a.service_id || "Service";
}
function plannerStatusClass(status: string) {
  if (status === "pending_confirmation") return "border-amber-200 bg-amber-50 text-amber-900";
  if (status === "confirmed") return "border-emerald-200 bg-emerald-50 text-emerald-900";
  if (status === "completed") return "border-sky-200 bg-sky-50 text-sky-900";
  if (status === "cancelled") return "border-slate-200 bg-slate-100 text-slate-500";
  if (status === "no_show") return "border-rose-200 bg-rose-50 text-rose-900";
  return "border-slate-200 bg-white text-slate-700";
}
function dayRangeLabel(day: string, locale: string, queue?: boolean) {
  if (!queue) return new Date(`${day}T00:00:00`).toLocaleDateString(locale, { weekday: "long", month: "short", day: "numeric" });
  const end = addDays(day, 6);
  const startDate = new Date(`${day}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  return `${startDate.toLocaleDateString(locale, { month: "short", day: "numeric" })} - ${endDate.toLocaleDateString(locale, { month: "short", day: "numeric" })}`;
}

function localIsoDate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isoToday() {
  return localIsoDate(new Date());
}
function addDays(day: string, delta: number) {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + delta);
  return localIsoDate(d);
}
function bounds(day: string) {
  const start = `${day}T00:00:00.000Z`;
  const d = new Date(start);
  d.setUTCDate(d.getUTCDate() + 1);
  return { start, end: d.toISOString() };
}
function parseStatuses(v: string | null, fallback: Status[] = []) {
  if (!v || v === "all") return fallback;
  return v.split(",").filter((x): x is Status => STATUSS.includes(x as Status));
}
function fmtStatuses(v: Status[]) {
  return v.length ? v.join(",") : "";
}
function parseReqId(msg: string) {
  return /request_id=([A-Za-z0-9-]+)/.exec(msg)?.[1] ?? null;
}
function humanError(err: unknown) {
  const raw = err instanceof Error ? err.message : String(err);
  const requestId = parseReqId(raw);
  return requestId
    ? { requestId, text: `Action failed. Request ID: ${requestId}. Please retry. If it persists, send this ID to support.` }
    : { requestId: null, text: "Action failed. Please retry." };
}
function riskTone(r?: string | null) {
  return r === "high" ? "risk-high" : r === "medium" ? "risk-medium" : r ? "risk-low" : "default";
}
function statusTone(s: string) {
  return s === "pending_confirmation" ? "queue-warning" : s === "confirmed" ? "queue-ok" : "default";
}
function sourceBadge(s?: string | null) {
  if (s === "telegram") return "TG";
  if (s === "whatsapp") return "WA";
  if (s === "web") return "WEB";
  return "CRM";
}
function copyText(v: string) {
  return navigator.clipboard?.writeText(v);
}
function patchLists(qc: ReturnType<typeof useQueryClient>, fn: (a: Appointment) => Appointment) {
  const snaps = qc.getQueriesData<ListResp>({ queryKey: ["manager-calendar-appts"] });
  snaps.forEach(([k, d]) => {
    if (!d?.items) return;
    qc.setQueryData(k, { ...d, items: d.items.map(fn) });
  });
  return snaps;
}
function matchesQ(a: Appointment, q: string) {
  const x = q.trim().toLowerCase();
  if (!x) return true;
  return [a.customer_name, a.customer_phone, a.service_name, a.staff_name].some((v) => String(v || "").toLowerCase().includes(x));
}
function sortQueue(items: Appointment[]) {
  return [...items].sort((a, b) => {
    const r = riskRank(b.customer_risk_tier) - riskRank(a.customer_risk_tier);
    return r || a.start_at.localeCompare(b.start_at);
  });
}

export function ManagerCalendarPage() {
  const { t, i18n } = useTranslation();
  const { bootstrap } = useTenantAuth();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const exact = useMatch({ path: "/app/calendar", end: true });
  const mToday = useMatch("/app/calendar/today");
  const mDay = useMatch("/app/calendar/day");
  const mQueue = useMatch("/app/calendar/queue");
  const mDetail = useMatch("/app/calendar/appointment/:id");
  const today = isoToday();
  const tab: Tab = mToday ? "today" : mDay ? "day" : mQueue ? "queue" : ((sp.get("view_from") as Tab) || "today");

  const [density, setDensity] = useState<"comfortable" | "compact">(() => (localStorage.getItem("manager_calendar_density") === "compact" ? "compact" : "comfortable"));
  const [plannerView, setPlannerView] = useState<PlannerView>(() => (localStorage.getItem("manager_calendar_planner_view") === "agenda" ? "agenda" : "grid"));
  const [serviceFilter, setServiceFilter] = useState("");
  const [specialistMode, setSpecialistMode] = useState<SpecialistMode>("all");
  const [gridOnlyFree, setGridOnlyFree] = useState(false);
  const [gridHideQuietHours, setGridHideQuietHours] = useState(true);
  const [quickCreate, setQuickCreate] = useState<QuickCreateDrawerState>({ open: false });
  const [actionDialog, setActionDialog] = useState<ActionDialog>({ open: false });
  const [rescheduleDialog, setRescheduleDialog] = useState<RescheduleDialog>({ open: false });
  const [cardErrors, setCardErrors] = useState<Record<string, { text: string; requestId?: string | null; retry: { action: Action; reason?: string; new_start_at?: string } }>>({});
  const [waitlistNotice, setWaitlistNotice] = useState("");

  useEffect(() => {
    localStorage.setItem("manager_calendar_density", density);
  }, [density]);
  useEffect(() => {
    localStorage.setItem("manager_calendar_planner_view", plannerView);
  }, [plannerView]);

  const branchId = sp.get("branch_id") ?? (bootstrap?.branches?.[0]?.id ?? "");
  const date = sp.get("date") ?? today;
  const q = sp.get("q") ?? "";
  const staffId = sp.get("staff_id") ?? "";
  const statuses = parseStatuses(sp.get("status"), tab === "queue" ? ["pending_confirmation", "confirmed"] : []);
  const activeDay = date;
  const dayB = bounds(activeDay);
  const queueStart = date || today;
  const queueB = useMemo(() => ({ start: bounds(queueStart).start, end: bounds(addDays(queueStart, 8)).start }), [queueStart]);

  const setParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setSp(next);
  };

  const list = useQuery({
    queryKey: ["manager-calendar-appts", tab, branchId, tab === "queue" ? queueB.start : dayB.start, tab === "queue" ? queueB.end : dayB.end],
    queryFn: () => {
      const b = tab === "queue" ? queueB : dayB;
      return apiGet<ListResp>(`/v3/calendar/appointments?size=${tab === "queue" ? 500 : 200}${branchId ? `&branch_id=${encodeURIComponent(branchId)}` : ""}&start_at=${encodeURIComponent(b.start)}&end_at=${encodeURIComponent(b.end)}`);
    },
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    refetchInterval: tab === "today" ? 30_000 : false,
  });

  const waitlist = useQuery({
    queryKey: ["manager-calendar-waitlist", branchId, activeDay],
    queryFn: () => apiGet<{ items: WaitlistItem[] }>(`/v3/calendar/waitlist?day=${encodeURIComponent(activeDay)}${branchId ? `&branch_id=${encodeURIComponent(branchId)}` : ""}`),
    enabled: tab !== "queue",
  });

  const detailId = mDetail?.params.id;
  const detail = useQuery({ queryKey: ["manager-calendar-detail", detailId], queryFn: () => apiGet<Appointment>(`/v3/calendar/appointments/${detailId}`), enabled: !!detailId });
  const timeline = useQuery({
    queryKey: ["manager-calendar-timeline", detailId],
    queryFn: () => apiGet<{ items: TimelineItem[] }>(`/v3/calendar/appointments/${detailId}/timeline`),
    enabled: !!detailId,
  });
  const staffCatalog = useQuery({
    queryKey: ["manager-calendar-staff-options"],
    queryFn: () => apiGet<{ items: StaffOptionRow[] }>("/v3/staff"),
    staleTime: 30_000,
  });

  const staffOptions = useMemo(() => {
    const map = new Map<string, string>();
    (staffCatalog.data?.items ?? [])
      .filter((row) => (row.is_active ?? true) && (!branchId || !row.branch_id || row.branch_id === branchId))
      .forEach((row) => {
        if (!row.id) return;
        map.set(row.id, row.full_name || row.id);
      });
    (list.data?.items ?? []).forEach((a) => a.staff_id && map.set(a.staff_id, a.staff_name || a.staff_id));
    if (detail.data?.staff_id) map.set(detail.data.staff_id, detail.data.staff_name || detail.data.staff_id);
    return [...map.entries()].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [branchId, detail.data, list.data?.items, staffCatalog.data?.items]);

  const serviceOptions = useMemo(() => {
    const map = new Map<string, string>();
    (list.data?.items ?? []).forEach((a) => {
      const key = serviceKeyFor(a);
      const label = serviceLabelFor(a);
      if (label) map.set(key, label);
    });
    if (detail.data) map.set(serviceKeyFor(detail.data), serviceLabelFor(detail.data));
    return [...map.entries()].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [detail.data, list.data?.items]);

  const serviceScopedStaffIds = useMemo(() => {
    if (!serviceFilter) return new Set<string>();
    return new Set(
      (list.data?.items ?? [])
        .filter((a) => a.staff_id && serviceKeyFor(a) === serviceFilter)
        .map((a) => a.staff_id)
    );
  }, [list.data?.items, serviceFilter]);

  const filteredStaffOptions = useMemo(() => {
    if (specialistMode !== "by_service" || !serviceFilter) return staffOptions;
    return staffOptions.filter((s) => serviceScopedStaffIds.has(s.id));
  }, [serviceFilter, serviceScopedStaffIds, specialistMode, staffOptions]);

  const effectiveStaffId = specialistMode === "specific" ? staffId : "";

  const dayLayout = useQuery({
    queryKey: ["manager-calendar-day-layout", branchId, activeDay, effectiveStaffId],
    queryFn: () => apiGet<DayLayoutResp>(`/v3/calendar/day-layout?branch_id=${encodeURIComponent(branchId)}&day=${encodeURIComponent(activeDay)}${effectiveStaffId ? `&staff_id=${encodeURIComponent(effectiveStaffId)}` : ""}`),
    enabled: tab !== "queue" && !!branchId,
    staleTime: 15_000,
  });

  const visible = useMemo(() => {
    let items = list.data?.items ?? [];
    if (tab === "queue") items = sortQueue(items); else items = [...items].sort((a, b) => a.start_at.localeCompare(b.start_at));
    if (statuses.length) items = items.filter((x) => statuses.includes(x.status as Status));
    if (serviceFilter) items = items.filter((x) => serviceKeyFor(x) === serviceFilter);
    if (effectiveStaffId) items = items.filter((x) => x.staff_id === effectiveStaffId);
    return items.filter((x) => matchesQ(x, q));
  }, [effectiveStaffId, list.data?.items, q, serviceFilter, statuses, tab]);

  const grouped = useMemo(() => {
    if (tab === "queue") return [];
    const map = new Map<string, Appointment[]>();
    visible.forEach((a) => {
      const key = new Date(a.start_at).toLocaleTimeString(i18n.resolvedLanguage || i18n.language, { hour: "numeric", minute: "2-digit" });
      map.set(key, [...(map.get(key) ?? []), a]);
    });
    return [...map.entries()].map(([label, items]) => ({ label, items }));
  }, [visible, tab, i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    if (specialistMode !== "specific") return;
    if (!staffId) return;
    if (filteredStaffOptions.some((s) => s.id === staffId)) return;
    setParams({ staff_id: null });
  }, [filteredStaffOptions, specialistMode, staffId]);

  const transition = useMutation({
    mutationFn: (payload: { id: string; action: Action; reason?: string; new_start_at?: string }) => apiPost<Appointment>(`/v3/calendar/appointments/${payload.id}/transition`, payload),
    onMutate: (payload) => {
      const snaps = patchLists(qc, (a) => {
        if (a.id !== payload.id) return a;
        const nextStatus = nextStatusFromAction(a.status, payload.action);
        const out = { ...a, status: nextStatus, allowed_actions: allowedActionsForStatus(nextStatus), updated_at: new Date().toISOString() };
        if (payload.new_start_at) {
          const dur = new Date(a.end_at).getTime() - new Date(a.start_at).getTime();
          out.start_at = payload.new_start_at;
          out.end_at = new Date(new Date(payload.new_start_at).getTime() + dur).toISOString();
        }
        return out;
      });
      setCardErrors((p) => {
        const n = { ...p };
        delete n[payload.id];
        return n;
      });
      return { snaps, payload };
    },
    onError: (err, _vars, ctx) => {
      (ctx?.snaps ?? []).forEach(([k, d]) => qc.setQueryData(k, d));
      const e = humanError(err);
      if (ctx?.payload?.id) {
        setCardErrors((p) => ({ ...p, [ctx.payload.id]: { text: e.text, requestId: e.requestId, retry: { action: ctx.payload.action, reason: ctx.payload.reason, new_start_at: ctx.payload.new_start_at } } }));
      }
    },
    onSuccess: (row) => {
      patchLists(qc, (a) => (a.id === row.id ? { ...a, ...row } : a));
      qc.setQueryData(["manager-calendar-detail", row.id], row);
    },
    onSettled: async (_d, _e, vars) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["manager-calendar-appts"] }),
        qc.invalidateQueries({ queryKey: ["manager-calendar-detail", vars.id] }),
        qc.invalidateQueries({ queryKey: ["manager-calendar-timeline", vars.id] }),
      ]);
    },
  });

  const waitlistPatch = useMutation({
    mutationFn: ({ id, status }: { id: string; status: WaitlistStatus }) => apiPatch(`/v3/calendar/waitlist/${id}`, { status }),
    onSuccess: async () => qc.invalidateQueries({ queryKey: ["manager-calendar-waitlist"] }),
  });
  const waitlistLink = useMutation({
    mutationFn: (id: string) => apiPost<{ url: string }>(`/v3/calendar/waitlist/${id}/booking-link`, {}),
    onSuccess: async (data) => {
      const abs = `${window.location.origin}${data.url}`;
      try {
        await copyText(abs);
        setWaitlistNotice(t("manager.calendar.waitlist.copied", "Booking link copied."));
      } catch {
        setWaitlistNotice(abs);
      }
    },
    onError: (e) => setWaitlistNotice(String(e)),
  });

  const baseSearch = useMemo(() => {
    const next = new URLSearchParams(sp);
    next.delete("view_from");
    return next.toString();
  }, [sp]);

  const openDetail = (id: string) => {
    const next = new URLSearchParams(sp);
    next.set("view_from", tab);
    navigate(`/app/calendar/appointment/${id}?${next.toString()}`);
  };
  const closeDetail = () => {
    const next = new URLSearchParams(sp);
    const from = (next.get("view_from") as Tab) || "today";
    next.delete("view_from");
    navigate(`/app/calendar/${from}${next.toString() ? `?${next.toString()}` : ""}`);
  };
  const launchAction = (a: Appointment, action: Action) => {
    if (action === "reschedule") {
      setRescheduleDialog({ open: true, appointment: a, day: a.start_at.slice(0, 10), staff_id: a.staff_id || "" });
      return;
    }
    setActionDialog({ open: true, appointment: a, action });
  };
  const submitAction = (a: Appointment, payload: { action: Action; reason?: string; new_start_at?: string }) => transition.mutateAsync({ id: a.id, ...payload });
  const dayNavAnchor = tab === "queue" ? queueStart : activeDay;
  const plannerDateLabel = dayRangeLabel(dayNavAnchor, i18n.resolvedLanguage || i18n.language, tab === "queue");
  const movePlannerDay = (delta: number) => setParams({ date: addDays(dayNavAnchor, delta) });
  const resetPlannerToday = () => setParams({ date: today });
  const openQuickCreate = (payload: { day?: string; minuteHint?: number; staff_id?: string } = {}) => {
    setQuickCreate({ open: true, day: payload.day || activeDay, minuteHint: payload.minuteHint, staff_id: payload.staff_id || effectiveStaffId || undefined });
  };
  const setFocusedStaff = (nextStaffId: string) => {
    setSpecialistMode(nextStaffId ? "specific" : "all");
    setParams({ staff_id: nextStaffId || null });
  };
  const plannerStaffColumns = useMemo(() => {
    const map = new Map<string, string>();
    (dayLayout.data?.items ?? []).forEach((row) => row.staff_id && map.set(row.staff_id, row.staff_name || row.staff_id));
    visible.forEach((a) => {
      if (a.staff_id) map.set(a.staff_id, a.staff_name || a.staff_id);
    });
    const source = specialistMode === "by_service" ? filteredStaffOptions : staffOptions;
    source.forEach((s) => map.set(s.id, s.label));
    if (effectiveStaffId && staffOptions.find((s) => s.id === effectiveStaffId)) {
      const current = staffOptions.find((s) => s.id === effectiveStaffId)!;
      return [{ id: current.id, label: current.label }];
    }
    return [...map.entries()].map(([id, label]) => ({ id, label })).slice(0, 8);
  }, [dayLayout.data?.items, effectiveStaffId, filteredStaffOptions, specialistMode, staffOptions, visible]);

  if (exact) return <Navigate to={`/app/calendar/today${sp.toString() ? `?${sp.toString()}` : ""}`} replace />;

  return (
    <div className="grid gap-4">
      <section className="manager-ribbon">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-slate-300">{t("manager.calendar.ribbon.overline", "Calendar Decision Mode")}</p>
            <h1 className="mt-1 text-2xl font-semibold">{t("manager.calendar.title", "Calendar Operations")}</h1>
            <p className="mt-1 text-sm text-slate-200">{t("manager.calendar.subtitle", "Prioritize actions by risk, status, and schedule rhythm.")}</p>
          </div>
          <div className="flex gap-2">
            <button type="button" className={cn("btn secondary", density === "comfortable" && "ring-2 ring-white/30")} onClick={() => setDensity("comfortable")}>Comfortable</button>
            <button type="button" className={cn("btn secondary", density === "compact" && "ring-2 ring-white/30")} onClick={() => setDensity("compact")}>Compact</button>
          </div>
        </div>
      </section>

      <section className="panel p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {(["today", "day", "queue"] as Tab[]).map((x) => (
              <Link key={x} className={cn("manager-filter-chip", tab === x && "active")} to={`/app/calendar/${x}${baseSearch ? `?${baseSearch}` : ""}`}>
                {x === "today" ? t("manager.calendar.tab.today", "Today") : x === "day" ? t("manager.calendar.tab.day", "Day") : t("manager.calendar.tab.queue", "Queue")}
              </Link>
            ))}
            {tab !== "queue" ? <a className="manager-filter-chip" href="#calendar-waitlist">{t("manager.calendar.tab.waitlist", "Waitlist")}</a> : null}
          </div>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="text-xs text-slate-500">{t("manager.calendar.branch", "Branch")}</label>
              <select className="input mt-1" value={branchId} onChange={(e) => setParams({ branch_id: e.target.value || null })}>
                <option value="">{t("manager.calendar.allBranches", "All branches")}</option>
                {(bootstrap?.branches ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">{t("manager.calendar.search", "Quick search")}</label>
              <input className="input mt-1" value={q} placeholder={t("manager.calendar.searchPlaceholder", "Customer, phone, service")} onChange={(e) => setParams({ q: e.target.value || null })} />
            </div>
            <div>
              <label className="text-xs text-slate-500">{t("manager.calendar.staff", "Staff")} {specialistMode !== "specific" ? "(focus)" : ""}</label>
              <select className="input mt-1" value={staffId} onChange={(e) => setFocusedStaff(e.target.value)}>
                <option value="">{t("manager.calendar.staffAll", "All staff")}</option>
                {filteredStaffOptions.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button type="button" className="btn secondary w-full" onClick={() => list.refetch()}>{tab === "today" ? t("manager.calendar.refresh", "Refresh") : t("manager.calendar.refresh", "Refresh")}</button>
            </div>
          </div>
        </div>
      </section>

      <section className="panel p-4">
        <DateStrip locale={i18n.resolvedLanguage || i18n.language} activeDay={activeDay} baseDay={tab === "queue" ? date : activeDay} queue={tab === "queue"} onPick={(d) => setParams({ date: d })} />
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className={cn("manager-filter-chip", statuses.length === 0 && "active")} onClick={() => setParams({ status: null })}>{t("manager.calendar.status.all", "All statuses")}</button>
          {STATUSS.map((s) => (
            <button key={s} type="button" className={cn("manager-filter-chip", statuses.includes(s) && "active")} onClick={() => {
              const next = statuses.includes(s) ? statuses.filter((x) => x !== s) : [...statuses, s];
              setParams({ status: fmtStatuses(next) || null });
            }}>
              {t(`manager.calendar.status.${s}`, s)}
            </button>
          ))}
        </div>
      </section>

      <section className="panel p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="btn secondary" onClick={() => movePlannerDay(tab === "queue" ? -7 : -1)} aria-label="Previous period">&lt;</button>
            <button type="button" className="btn secondary" onClick={resetPlannerToday}>Today</button>
            <button type="button" className="btn secondary" onClick={() => movePlannerDay(tab === "queue" ? 7 : 1)} aria-label="Next period">&gt;</button>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">{plannerDateLabel}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {tab !== "queue" ? (
              <div className="flex rounded-xl border border-slate-200 bg-white p-1">
                <button type="button" className={cn("rounded-lg px-3 py-1.5 text-sm", plannerView === "grid" ? "bg-slate-900 text-white" : "text-slate-600")} onClick={() => setPlannerView("grid")}>Grid</button>
                <button type="button" className={cn("rounded-lg px-3 py-1.5 text-sm", plannerView === "agenda" ? "bg-slate-900 text-white" : "text-slate-600")} onClick={() => setPlannerView("agenda")}>Agenda</button>
              </div>
            ) : null}
            <button type="button" className="btn" onClick={() => openQuickCreate()}>+ New booking</button>
          </div>
        </div>

        {tab !== "queue" ? (
          <div className="mt-4 grid gap-4 xl:grid-cols-[300px,1fr]">
            <aside className="grid gap-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Filters</p>
                <div className="mt-3 grid gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Service</label>
                    <select className="input mt-1" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
                      <option value="">All services</option>
                      {serviceOptions.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Specialist</p>
                    <div className="mt-2 grid gap-2">
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="radio" name="specialist-mode" checked={specialistMode === "all"} onChange={() => { setSpecialistMode("all"); setParams({ staff_id: null }); }} />
                        <span>All</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="radio" name="specialist-mode" checked={specialistMode === "by_service"} onChange={() => { setSpecialistMode("by_service"); setParams({ staff_id: null }); }} />
                        <span>By service</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="radio" name="specialist-mode" checked={specialistMode === "specific"} onChange={() => setSpecialistMode("specific")} />
                        <span>Specific</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Focused specialist</label>
                    <select className="input mt-1" value={staffId} disabled={specialistMode !== "specific"} onChange={(e) => setParams({ staff_id: e.target.value || null })}>
                      <option value="">All staff</option>
                      {filteredStaffOptions.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                    {specialistMode === "by_service" && serviceFilter ? <p className="mt-1 text-xs text-slate-500">Showing staff seen on this service for selected period.</p> : null}
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={gridOnlyFree} onChange={(e) => setGridOnlyFree(e.target.checked)} />
                    <span>Only free windows</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={gridHideQuietHours} onChange={(e) => setGridHideQuietHours(e.target.checked)} />
                    <span>Hide non-working hours</span>
                  </label>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Legend</p>
                <div className="mt-3 grid gap-2 text-sm">
                  <LegendItem tone="border-slate-200 bg-white text-slate-700" label="Free" />
                  <LegendItem tone={plannerStatusClass("confirmed")} label="Busy (confirmed)" />
                  <LegendItem tone={plannerStatusClass("pending_confirmation")} label="Awaiting confirmation" />
                  <LegendItem tone={plannerStatusClass("cancelled")} label="Cancelled" />
                  <LegendItem tone="border-slate-300 bg-slate-100/70 text-slate-500" label="Break / unavailable (not yet modeled)" />
                </div>
              </div>
            </aside>

            <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
              {dayLayout.error ? <p className="mb-3 text-sm text-red-600">Failed to load staff working hours layout. Grid may be incomplete.</p> : null}
              <PlannerCanvas
                density={density}
                mode={plannerView}
                locale={i18n.resolvedLanguage || i18n.language}
                activeDay={activeDay}
                appointments={visible}
                staffColumns={plannerStaffColumns}
                staffLayout={dayLayout.data?.items ?? []}
                hideQuietHours={gridHideQuietHours}
                onlyFreeWindows={gridOnlyFree}
                onOpenAppointment={openDetail}
                onCreateAt={(minute, staff) => {
                  openQuickCreate({ minuteHint: minute, staff_id: staff?.id });
                }}
              />
              <p className="mt-3 text-xs text-slate-500">
                Grid combines appointments with staff working hours and time-off blocks for the selected day.
              </p>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-sm text-slate-500">Queue mode stays list-first. Use the date range controls for the next/previous week window.</p>
        )}
      </section>

      <section className="panel p-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold">{tab === "queue" ? t("manager.calendar.queue.title", "Queue (next 7 days)") : t("manager.calendar.appointmentsTitle", "Appointments ({{day}})", { day: activeDay })}</h2>
            <p className="text-sm text-slate-500">{tab === "queue" ? t("manager.calendar.queue.subtitle", "Needs action across days") : t("manager.calendar.itemsCount", "{{count}} item(s)", { count: visible.length })}</p>
          </div>
          {tab === "today" ? <p className="text-xs text-slate-500">{t("manager.calendar.autorefresh", "Auto-refresh every 30s")}</p> : null}
        </div>
        {list.isLoading ? <div className="skeleton mt-3 h-24 w-full" /> : null}
        {list.error ? <p className="mt-3 text-sm text-red-600">{String(list.error)}</p> : null}
        {!list.isLoading && !list.error ? (
          <div className="mt-3 grid gap-3">
            {tab === "queue" ? visible.map((a) => <AppointmentCard key={a.id} a={a} density={density} locale={i18n.resolvedLanguage || i18n.language} t={t} pending={transition.isPending} error={cardErrors[a.id]} onAction={launchAction} onRetry={(r) => void submitAction(a, r)} onOpenDetail={() => openDetail(a.id)} queue />) : grouped.map((g) => (
              <div key={g.label} className="grid gap-2">
                <div className="rounded-lg bg-slate-100 px-3 py-1 text-xs font-semibold uppercase text-slate-600">{g.label}</div>
                {g.items.map((a) => <AppointmentCard key={a.id} a={a} density={density} locale={i18n.resolvedLanguage || i18n.language} t={t} pending={transition.isPending} error={cardErrors[a.id]} onAction={launchAction} onRetry={(r) => void submitAction(a, r)} onOpenDetail={() => openDetail(a.id)} />)}
              </div>
            ))}
            {!visible.length ? <p className="text-sm text-slate-500">{t("manager.calendar.noAppointments", "No appointments for this filter.")}</p> : null}
          </div>
        ) : null}
      </section>
      {tab !== "queue" ? (
        <section id="calendar-waitlist" className="panel p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">{t("manager.calendar.waitlist.title", "Waitlist ({{day}})", { day: activeDay })}</h2>
            <p className="text-sm text-slate-500">{t("manager.calendar.waitlist.count", "{{count}} item(s)", { count: waitlist.data?.items?.length ?? 0 })}</p>
          </div>
          {waitlistNotice ? <p className="mt-2 text-xs text-emerald-700">{waitlistNotice}</p> : null}
          {waitlist.isLoading ? <p className="mt-2 text-sm">{t("loading", "Loading...")}</p> : null}
          <div className="mt-3 grid gap-2">
            {(waitlist.data?.items ?? []).map((w) => (
              <article key={w.id} className="manager-card">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold">{w.customer_name || t("manager.calendar.unknownClient", "Unknown client")}</p>
                    <p className="mt-1 text-sm text-slate-600">{w.service_name || t("manager.calendar.defaultService", "Service")}{w.preferred_staff_name ? ` • ${w.preferred_staff_name}` : ""}</p>
                    {w.customer_phone ? <a className="mt-1 block text-xs text-sky-700 underline" href={`tel:${w.customer_phone}`}>{w.customer_phone}</a> : null}
                    {w.notes ? <p className="mt-1 text-xs text-slate-500">{w.notes}</p> : null}
                  </div>
                  <StatusPill label={t(`manager.calendar.waitlist.status.${w.status}`, w.status)} />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" className="btn secondary" disabled={waitlistLink.isPending} onClick={() => waitlistLink.mutate(w.id)}>{t("manager.calendar.waitlist.action.copyLink", "Copy booking link")}</button>
                  <button type="button" className="btn secondary" disabled={waitlistPatch.isPending} onClick={() => waitlistPatch.mutate({ id: w.id, status: "contacted" })}>{t("manager.calendar.waitlist.action.contacted", "Mark contacted")}</button>
                  <button type="button" className="btn secondary" disabled={waitlistPatch.isPending} onClick={() => waitlistPatch.mutate({ id: w.id, status: "closed" })}>{t("manager.calendar.waitlist.action.close", "Close")}</button>
                </div>
              </article>
            ))}
            {!waitlist.isLoading && !(waitlist.data?.items?.length ?? 0) ? <p className="text-sm text-slate-500">{t("manager.calendar.waitlist.empty", "No waitlist entries for this day.")}</p> : null}
          </div>
        </section>
      ) : null}

      {quickCreate.open ? (
        <QuickCreateDrawer
          state={quickCreate}
          t={t}
          locale={i18n.resolvedLanguage || i18n.language}
          branches={(bootstrap?.branches ?? []) as SimpleBranch[]}
          initialBranchId={branchId}
          initialServiceId={serviceFilter.startsWith("id:") ? serviceFilter.slice(3) : ""}
          initialStaffId={quickCreate.staff_id || effectiveStaffId}
          onClose={() => setQuickCreate({ open: false })}
          onCreated={(row) => {
            qc.invalidateQueries({ queryKey: ["manager-calendar-appts"] });
            qc.invalidateQueries({ queryKey: ["manager-calendar-day-layout"] });
            setQuickCreate({ open: false });
            openDetail(row.id);
          }}
        />
      ) : null}

      {actionDialog.open ? (
        <ActionDialogModal
          state={actionDialog}
          t={t}
          pending={transition.isPending}
          onClose={() => setActionDialog({ open: false })}
          onConfirm={async (reason) => {
            await submitAction(actionDialog.appointment, { action: actionDialog.action, reason });
            setActionDialog({ open: false });
          }}
        />
      ) : null}

      {rescheduleDialog.open ? (
        <RescheduleModal
          state={rescheduleDialog}
          t={t}
          locale={i18n.resolvedLanguage || i18n.language}
          staffOptions={staffOptions}
          pending={transition.isPending}
          onClose={() => setRescheduleDialog({ open: false })}
          onChangeDay={(d) => setRescheduleDialog((s) => s.open ? { ...s, day: d } : s)}
          onChangeStaff={(id) => setRescheduleDialog((s) => s.open ? { ...s, staff_id: id } : s)}
          onConfirm={async (newStartAt) => {
            await submitAction(rescheduleDialog.appointment, { action: "reschedule", new_start_at: newStartAt });
            setRescheduleDialog({ open: false });
          }}
        />
      ) : null}

      {detailId ? (
        <DetailOverlay
          appointment={detail.data}
          appointmentLoading={detail.isLoading}
          appointmentError={detail.error}
          timeline={timeline.data?.items ?? []}
          timelineLoading={timeline.isLoading}
          locale={i18n.resolvedLanguage || i18n.language}
          t={t}
          onClose={closeDetail}
          onAction={launchAction}
        />
      ) : null}
    </div>
  );
}

function DateStrip({ baseDay, activeDay, locale, queue, onPick }: { baseDay: string; activeDay: string; locale: string; queue?: boolean; onPick: (d: string) => void }) {
  const days = queue ? Array.from({ length: 7 }, (_, i) => addDays(baseDay, i)) : Array.from({ length: 13 }, (_, i) => addDays(baseDay, i - 6));
  const today = isoToday();
  return (
    <div className="manager-date-strip">
      {days.map((d) => (
        <button key={d} type="button" className={cn("manager-date-pill", d === activeDay && "active")} onClick={() => onPick(d)}>
          <div className="text-xs opacity-70">{new Date(`${d}T00:00:00`).toLocaleDateString(locale, { weekday: "short" })}</div>
          <div className="font-semibold">{new Date(`${d}T00:00:00`).toLocaleDateString(locale, { month: "short", day: "numeric" })}</div>
          <div className="text-[10px]">{d === today ? "Today" : " "}</div>
        </button>
      ))}
    </div>
  );
}

function LegendItem({ tone, label }: { tone: string; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className={cn("inline-block h-3 w-3 rounded-full border", tone)} />
      <span>{label}</span>
    </div>
  );
}

function PlannerCanvas({
  density,
  mode,
  locale,
  activeDay,
  appointments,
  staffColumns,
  staffLayout,
  hideQuietHours,
  onlyFreeWindows,
  onOpenAppointment,
  onCreateAt,
}: {
  density: "comfortable" | "compact";
  mode: PlannerView;
  locale: string;
  activeDay: string;
  appointments: Appointment[];
  staffColumns: Array<{ id: string; label: string }>;
  staffLayout: StaffDayLayout[];
  hideQuietHours: boolean;
  onlyFreeWindows: boolean;
  onOpenAppointment: (id: string) => void;
  onCreateAt: (minute: number, staff?: { id: string; label: string }) => void;
}) {
  const columns = staffColumns.length ? staffColumns : [{ id: "__all__", label: "All staff" }];
  const compact = density === "compact";
  const activeDayDate = new Date(`${activeDay}T00:00:00`);
  const activeStartMs = activeDayDate.getTime();
  const today = isoToday();
  const now = new Date();
  const isToday = activeDay === today;
  const items = useMemo(
    () => [...appointments].sort((a, b) => a.start_at.localeCompare(b.start_at)),
    [appointments]
  );
  const layoutByStaff = useMemo(() => new Map((staffLayout ?? []).map((row) => [row.staff_id, row])), [staffLayout]);
  const hasAnyWorkingWindows = useMemo(
    () => (staffLayout ?? []).some((row) => (row.working_windows?.length ?? 0) > 0),
    [staffLayout]
  );

  const agendaGroups = useMemo(() => {
    const groups = new Map<string, Appointment[]>();
    items.forEach((a) => {
      const label = new Date(a.start_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
      groups.set(label, [...(groups.get(label) ?? []), a]);
    });
    return [...groups.entries()].map(([label, rows]) => ({ label, rows }));
  }, [items, locale]);

  const gridRows = useMemo(() => {
    const relevant = items.filter((a) => a.start_at.slice(0, 10) === activeDay);
    const layoutMinutes = [...layoutByStaff.values()].flatMap((row) => [
      ...row.working_windows.flatMap((w) => [minutesSinceMidnight(new Date(w.start_at)), minutesSinceMidnight(new Date(w.end_at))]),
      ...row.time_off.flatMap((w) => [minutesSinceMidnight(new Date(w.start_at)), minutesSinceMidnight(new Date(w.end_at))]),
    ]);
    const dayMinutes = [
      ...relevant.flatMap((a) => [minutesSinceMidnight(new Date(a.start_at)), minutesSinceMidnight(new Date(a.end_at))]),
      ...layoutMinutes,
    ];
    const derivedStart = dayMinutes.length ? clamp(roundDown(Math.min(...dayMinutes)), 6 * 60, 22 * 60) : DEFAULT_GRID_START_HOUR * 60;
    const derivedEnd = dayMinutes.length ? clamp(roundUp(Math.max(...dayMinutes)), derivedStart + HALF_HOUR, 23 * 60) : DEFAULT_GRID_END_HOUR * 60;
    const startMinute = hideQuietHours ? derivedStart : DEFAULT_GRID_START_HOUR * 60;
    const endMinute = hideQuietHours ? derivedEnd : DEFAULT_GRID_END_HOUR * 60;
    const byColumn = new Map<string, Appointment[]>();
    columns.forEach((c) => {
      const rows = c.id === "__all__" ? relevant : relevant.filter((a) => a.staff_id === c.id);
      byColumn.set(c.id, rows);
    });

    const rows: Array<{
      minute: number;
      isNow: boolean;
      cells: Array<{ staff: { id: string; label: string }; appointment?: Appointment; startsHere: boolean; unavailable: boolean; unavailableReason?: string; availableBySchedule: boolean }>;
      occupiedCount: number;
      freeCount: number;
      workingCount: number;
    }> = [];

    for (let minute = startMinute; minute < endMinute; minute += HALF_HOUR) {
      const slotStart = activeStartMs + minute * 60_000;
      const slotEnd = slotStart + HALF_HOUR * 60_000;
      const cells = columns.map((staff) => {
        const layout = staff.id === "__all__" ? null : layoutByStaff.get(staff.id);
        const hasWorkingWindow = !layout
          ? true
          : (layout.working_windows ?? []).some((w) => {
            const wStart = new Date(w.start_at).getTime();
            const wEnd = new Date(w.end_at).getTime();
            return wStart < slotEnd && wEnd > slotStart;
          });
        const inTimeOff = !!layout && (layout.time_off ?? []).some((w) => {
          const wStart = new Date(w.start_at).getTime();
          const wEnd = new Date(w.end_at).getTime();
          return wStart < slotEnd && wEnd > slotStart;
        });
        const appt = (byColumn.get(staff.id) ?? []).find((a) => {
          const aStart = new Date(a.start_at).getTime();
          const aEnd = new Date(a.end_at).getTime();
          return aStart < slotEnd && aEnd > slotStart;
        });
        const startsHere = !!appt && new Date(appt.start_at).getTime() >= slotStart && new Date(appt.start_at).getTime() < slotEnd;
        const unavailable = !hasWorkingWindow || inTimeOff;
        const unavailableReason = inTimeOff ? "Break" : !hasWorkingWindow ? "Unavailable" : undefined;
        return { staff, appointment: appt, startsHere, unavailable, unavailableReason, availableBySchedule: hasWorkingWindow && !inTimeOff };
      });
      const occupiedCount = cells.filter((c) => c.appointment).length;
      const freeCount = cells.filter((c) => !c.appointment && c.availableBySchedule).length;
      const workingCount = cells.filter((c) => c.availableBySchedule).length;
      const isNowRow = isToday && minutesSinceMidnight(now) >= minute && minutesSinceMidnight(now) < minute + HALF_HOUR;
      if (hideQuietHours && occupiedCount === 0 && workingCount === 0) continue;
      if (onlyFreeWindows && freeCount === 0) continue;
      rows.push({ minute, isNow: isNowRow, cells, occupiedCount, freeCount, workingCount });
    }
    return rows;
  }, [activeDay, activeStartMs, columns, hideQuietHours, isToday, items, layoutByStaff, now, onlyFreeWindows]);

  if (mode === "agenda") {
    return (
      <div className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Agenda</p>
            <p className="text-sm text-slate-600">{items.length} appointment(s) in current filters</p>
          </div>
          <button type="button" className="btn secondary" onClick={() => onCreateAt(DEFAULT_GRID_START_HOUR * 60)}>
            + New from agenda
          </button>
        </div>
        {!agendaGroups.length ? <p className="text-sm text-slate-500">No appointments for current filters.</p> : null}
        {agendaGroups.map((g) => (
          <div key={g.label} className="grid gap-2">
            <div className="rounded-xl bg-slate-100 px-3 py-1 text-xs font-semibold uppercase text-slate-600">{g.label}</div>
            {g.rows.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => onOpenAppointment(a.id)}
                className={cn("w-full rounded-xl border text-left", compact ? "p-2" : "p-3", plannerStatusClass(a.status), a.status === "cancelled" && "line-through")}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className={cn("truncate font-semibold", compact ? "text-xs" : "text-sm")}>{a.customer_name || "Unknown client"}</p>
                    <p className="truncate text-xs opacity-80">{serviceLabelFor(a)} • {a.staff_name || a.staff_id || "Staff"}</p>
                  </div>
                  <div className="text-xs font-medium">
                    {new Date(a.start_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })} - {new Date(a.end_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })}
                  </div>
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">Day Grid</p>
          <p className="text-sm text-slate-600">Columns = specialists, rows = 30-minute slots</p>
        </div>
        <p className="text-xs text-slate-500">{onlyFreeWindows ? "Showing rows with free capacity" : "Showing all visible rows"}</p>
      </div>
      {!gridRows.length ? (
        <p className="text-sm text-slate-500">
          {!hasAnyWorkingWindows
            ? `No working hours configured for ${activeDay}.`
            : "No rows to display for current filters/toggles."}
        </p>
      ) : null}
      {gridRows.length ? (
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            <div
              className="grid gap-px rounded-2xl border border-slate-200 bg-slate-200"
              style={{ gridTemplateColumns: `88px repeat(${columns.length}, minmax(140px, 1fr))` }}
            >
              <div className={cn("bg-slate-50 px-2 text-xs font-semibold uppercase tracking-wide text-slate-500", compact ? "py-1.5" : "py-2")}>Time</div>
              {columns.map((c) => (
                <div key={c.id} className={cn("bg-slate-50 px-2 text-xs font-semibold uppercase tracking-wide text-slate-500", compact ? "py-1.5" : "py-2")}>
                  <div className="truncate">{c.label}</div>
                </div>
              ))}
              {gridRows.map((row) => (
                <Fragment key={row.minute}>
                  <div className={cn("px-2 text-xs text-slate-600", compact ? "py-1.5" : "py-2", row.isNow ? "bg-rose-50 text-rose-700" : "bg-white")}>
                    <div className="font-semibold">{minuteLabel(row.minute, locale)}</div>
                    <div className="text-[10px]">{row.freeCount} free</div>
                  </div>
                  {row.cells.map((cell) => {
                    const a = cell.appointment;
                    if (!a) {
                      if (cell.unavailable) {
                        return (
                          <div
                            key={`${row.minute}-${cell.staff.id}`}
                            className={cn(
                              compact ? "min-h-[44px]" : "min-h-[60px]",
                              "bg-slate-100 px-2 text-left text-slate-500",
                              compact ? "py-1.5" : "py-2",
                              "bg-[linear-gradient(135deg,rgba(148,163,184,0.18)_25%,transparent_25%,transparent_50%,rgba(148,163,184,0.18)_50%,rgba(148,163,184,0.18)_75%,transparent_75%,transparent)] bg-[length:10px_10px]",
                              row.isNow && "ring-1 ring-inset ring-rose-200"
                            )}
                          >
                            <div className="text-[11px] font-medium">{cell.unavailableReason || "Unavailable"}</div>
                          </div>
                        );
                      }
                      return (
                        <button
                          key={`${row.minute}-${cell.staff.id}`}
                          type="button"
                          onClick={() => onCreateAt(row.minute, cell.staff.id === "__all__" ? undefined : cell.staff)}
                          className={cn(
                            compact ? "min-h-[44px]" : "min-h-[60px]",
                            "bg-white px-2 text-left transition hover:bg-emerald-50",
                            compact ? "py-1.5" : "py-2",
                            row.isNow && "ring-1 ring-inset ring-rose-200"
                          )}
                        >
                          <div className={cn("font-medium text-slate-700", compact ? "text-[11px]" : "text-xs")}>Free</div>
                          <div className="text-[11px] text-slate-500">Create booking</div>
                        </button>
                      );
                    }
                    return (
                      <button
                        key={`${row.minute}-${cell.staff.id}`}
                        type="button"
                        onClick={() => onOpenAppointment(a.id)}
                        className={cn(
                          compact ? "min-h-[44px]" : "min-h-[60px]",
                          "border-l-2 px-2 text-left",
                          compact ? "py-1.5" : "py-2",
                          plannerStatusClass(a.status),
                          a.status === "cancelled" && "line-through",
                          a.status === "pending_confirmation" && "border-dashed",
                          !cell.startsHere && "opacity-80"
                        )}
                      >
                        {cell.startsHere ? (
                          <>
                            <div className={cn("truncate font-semibold", compact ? "text-[11px]" : "text-xs")}>{a.customer_name || "Unknown client"}</div>
                            <div className="truncate text-[11px] opacity-80">{a.service_name || "Service"}</div>
                            <div className="mt-1 text-[10px] font-medium">
                              {new Date(a.start_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })}
                            </div>
                          </>
                        ) : (
                          <div className="text-[10px] uppercase tracking-wide opacity-70">Busy</div>
                        )}
                      </button>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AppointmentCard({
  a, density, locale, t, pending, error, onAction, onRetry, onOpenDetail, queue,
}: {
  a: Appointment; density: "comfortable" | "compact"; locale: string; t: any;
  pending: boolean; error?: { text: string; requestId?: string | null; retry: { action: Action; reason?: string; new_start_at?: string } };
  onAction: (a: Appointment, x: Action) => void; onRetry: (p: { action: Action; reason?: string; new_start_at?: string }) => void; onOpenDetail: () => void; queue?: boolean;
}) {
  const actions = (a.allowed_actions && a.allowed_actions.length ? a.allowed_actions : allowedActionsForStatus(a.status)) as Action[];
  const primary = primaryActionForStatus(a.status);
  const secondary = actions.filter((x) => x !== primary);
  return (
    <article className={cn("manager-card", density === "compact" && "p-3")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button type="button" className="min-w-0 flex-1 text-left" onClick={onOpenDetail}>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={cn("font-semibold", density === "compact" ? "text-sm" : "text-base")}>{a.customer_name || t("manager.calendar.unknownClient", "Unknown client")}</h3>
            {queue ? <span className="text-xs text-slate-500">{new Date(a.start_at).toLocaleDateString(locale, { month: "short", day: "numeric" })}</span> : null}
          </div>
          <p className={cn("mt-1 text-slate-600", density === "compact" ? "text-xs" : "text-sm")}>{a.service_name || t("manager.calendar.defaultService", "Service")} {t("manager.calendar.withStaff", "with")} {a.staff_name || t("manager.calendar.defaultStaff", "Staff")}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <StatusPill label={t(`manager.calendar.status.${a.status}`, a.status)} tone={statusTone(a.status) as never} />
            {a.customer_risk_tier ? <StatusPill label={t("manager.calendar.riskPill", "risk: {{tier}}", { tier: t(`manager.clients.risk.${a.customer_risk_tier}`, a.customer_risk_tier) })} tone={riskTone(a.customer_risk_tier) as never} /> : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
            {a.customer_phone ? <a href={`tel:${a.customer_phone}`} className="rounded-full border border-slate-200 px-2 py-1 text-sky-700">{a.customer_phone}</a> : null}
            {a.notes ? <span className="rounded-full border border-slate-200 px-2 py-1">Notes</span> : null}
            <span className="rounded-full border border-slate-200 px-2 py-1">{sourceBadge(a.source_channel)}</span>
          </div>
        </button>
        <div className="text-right text-sm">
          <div>{new Date(a.start_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })} - {new Date(a.end_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })}</div>
          <div className="mt-1 text-xs text-slate-500">{a.branch_timezone || ""}</div>
          <button type="button" className="mt-2 text-xs underline" onClick={onOpenDetail}>{t("manager.calendar.viewDetails", "View details")}</button>
        </div>
      </div>
      {error ? (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p>{error.text}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className="btn secondary" disabled={pending} onClick={() => onRetry(error.retry)}>{t("manager.calendar.retry", "Retry")}</button>
            {error.requestId ? <button type="button" className="btn secondary" onClick={() => void copyText(error.requestId || "")}>{t("manager.calendar.copyRequestId", "Copy Request ID")}</button> : null}
          </div>
        </div>
      ) : null}
      {actions.length ? (
        <div className="mt-3">
          <div className="hidden flex-wrap gap-2 sm:flex">
            {actions.map((x) => <button key={x} type="button" className="btn secondary" disabled={pending} onClick={() => onAction(a, x)}>{t(`manager.calendar.action.${x}`, x)}</button>)}
          </div>
          <div className="sm:hidden">
            <div className="flex gap-2">
              {primary ? <button type="button" className="btn flex-1" disabled={pending} onClick={() => onAction(a, primary)}>{t(`manager.calendar.action.${primary}`, primary)}</button> : null}
              {secondary.length ? <details className="flex-1"><summary className="btn secondary list-none text-center">{t("manager.calendar.moreActions", "More")}</summary><div className="mt-2 grid gap-2 rounded-xl border border-slate-200 bg-white p-2">{secondary.map((x) => <button key={x} type="button" className="btn secondary" disabled={pending} onClick={() => onAction(a, x)}>{t(`manager.calendar.action.${x}`, x)}</button>)}</div></details> : null}
            </div>
          </div>
        </div>
      ) : null}
    </article>
  );
}

function QuickCreateDrawer({
  state,
  t,
  locale,
  branches,
  initialBranchId,
  initialServiceId,
  initialStaffId,
  onClose,
  onCreated,
}: {
  state: Exclude<QuickCreateDrawerState, { open: false }>;
  t: any;
  locale: string;
  branches: SimpleBranch[];
  initialBranchId: string;
  initialServiceId?: string;
  initialStaffId?: string;
  onClose: () => void;
  onCreated: (row: Appointment) => void;
}) {
  const [branchId, setBranchId] = useState(initialBranchId || branches[0]?.id || "");
  const [day, setDay] = useState(state.day);
  const [serviceId, setServiceId] = useState(initialServiceId || "");
  const [staffId, setStaffId] = useState(initialStaffId || "");
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedStartAt, setSelectedStartAt] = useState("");
  const [inlineErr, setInlineErr] = useState("");

  const customers = useQuery({
    queryKey: ["manager-calendar-quick-create-customers", customerQuery],
    queryFn: () => apiGet<{ items: CustomerOption[] }>(`/v3/customers?archived=false&size=20${customerQuery.trim() ? `&search=${encodeURIComponent(customerQuery.trim())}` : ""}`),
    staleTime: 15_000,
  });
  const services = useQuery({
    queryKey: ["manager-calendar-quick-create-services", branchId],
    queryFn: () => apiGet<{ items: ServiceOptionRow[] }>(`/v3/services?size=100${branchId ? `&branch_id=${encodeURIComponent(branchId)}` : ""}`),
    enabled: !!branchId,
    staleTime: 30_000,
  });
  const staff = useQuery({
    queryKey: ["manager-calendar-quick-create-staff"],
    queryFn: () => apiGet<{ items: StaffOptionRow[] }>("/v3/staff"),
    staleTime: 30_000,
  });

  const activeStaff = useMemo(() => {
    return (staff.data?.items ?? []).filter((x) => (x.is_active ?? true) && (!branchId || !x.branch_id || x.branch_id === branchId));
  }, [branchId, staff.data?.items]);
  const activeServices = useMemo(() => {
    return (services.data?.items ?? []).filter((x) => x.is_active ?? true);
  }, [services.data?.items]);

  const slots = useQuery({
    queryKey: ["manager-calendar-quick-create-slots", branchId, serviceId, day, staffId],
    queryFn: () => apiGet<{ items: Slot[] }>(`/v3/calendar/smart-availability?branch_id=${encodeURIComponent(branchId)}&service_id=${encodeURIComponent(serviceId)}&day=${encodeURIComponent(day)}${staffId ? `&staff_id=${encodeURIComponent(staffId)}` : ""}`),
    enabled: !!branchId && !!serviceId && !!day,
  });

  useEffect(() => {
    setSelectedStartAt("");
    setInlineErr("");
  }, [branchId, day, serviceId, staffId]);

  useEffect(() => {
    if (!slots.data?.items?.length || selectedStartAt) return;
    const hintMinute = typeof state.minuteHint === "number" ? state.minuteHint : null;
    if (hintMinute == null) {
      setSelectedStartAt(slots.data.items[0].start_at);
      return;
    }
    const sorted = [...slots.data.items].sort((a, b) => {
      const aM = minutesSinceMidnight(new Date(a.start_at));
      const bM = minutesSinceMidnight(new Date(b.start_at));
      return Math.abs(aM - hintMinute) - Math.abs(bM - hintMinute);
    });
    setSelectedStartAt(sorted[0]?.start_at || "");
  }, [selectedStartAt, slots.data?.items, state.minuteHint]);

  const create = useMutation({
    mutationFn: () => {
      if (!branchId || !serviceId || !customerId || !selectedStartAt) throw new Error("Missing required fields");
      const slotStaffId = slots.data?.items?.find((s) => s.start_at === selectedStartAt)?.staff_id || staffId;
      if (!slotStaffId) throw new Error("Select a slot");
      return apiPost<Appointment>("/v3/calendar/appointments", {
        branch_id: branchId,
        service_id: serviceId,
        staff_id: slotStaffId,
        customer_id: customerId,
        start_at: selectedStartAt,
        source_channel: "crm",
        notes: notes.trim() || null,
      });
    },
    onSuccess: (row) => onCreated(row),
    onError: (e) => setInlineErr(String(e)),
  });

  const selectedCustomer = (customers.data?.items ?? []).find((c) => c.id === customerId);
  const selectedService = activeServices.find((s) => s.id === serviceId);
  const selectedSlot = slots.data?.items?.find((s) => s.start_at === selectedStartAt);
  const selectedSlotStaffName = activeStaff.find((s) => s.id === (selectedSlot?.staff_id || staffId))?.full_name;

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/35">
      <button type="button" className="absolute inset-0" onClick={onClose} aria-label="Close quick create drawer" />
      <div className="absolute inset-y-0 right-0 z-10 w-full max-w-xl overflow-y-auto border-l border-slate-200 bg-white p-4 shadow-2xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Quick Create</p>
            <h2 className="text-xl font-semibold">New appointment</h2>
            <p className="text-sm text-slate-500">Create from the calendar without leaving this page.</p>
          </div>
          <button type="button" className="btn secondary" onClick={onClose}>Close</button>
        </div>

        <div className="grid gap-4">
          <section className="rounded-2xl border border-slate-200 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs text-slate-500">Branch</label>
                <select className="input mt-1" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="">Select branch</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Day</label>
                <input className="input mt-1" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-slate-500">Service</label>
                <select className="input mt-1" value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
                  <option value="">Select service</option>
                  {activeServices.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Specialist (optional)</label>
                <select className="input mt-1" value={staffId} onChange={(e) => setStaffId(e.target.value)}>
                  <option value="">Auto / any</option>
                  {activeStaff.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                </select>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 p-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold">Client</h3>
              <Link
                className="text-xs text-sky-700 underline"
                to={`/app/clients/book?${new URLSearchParams({ day, ...(branchId ? { branch_id: branchId } : {}) }).toString()}`}
              >
                Full booking flow
              </Link>
            </div>
            <label className="mt-2 block text-xs text-slate-500">{t("manager.calendar.search", "Quick search")}</label>
            <input className="input mt-1" value={customerQuery} onChange={(e) => setCustomerQuery(e.target.value)} placeholder="Customer name or phone" />
            <div className="mt-2 max-h-44 overflow-y-auto rounded-xl border border-slate-200">
              {(customers.data?.items ?? []).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    setCustomerId(c.id);
                    if (!branchId && c.branch_id) setBranchId(c.branch_id);
                  }}
                  className={cn("flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50", customerId === c.id && "bg-emerald-50")}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{c.full_name}</span>
                    <span className="block truncate text-xs text-slate-500">{c.phone || "No phone"}</span>
                  </span>
                  {customerId === c.id ? <span className="text-xs font-semibold text-emerald-700">Selected</span> : null}
                </button>
              ))}
              {customers.isLoading ? <p className="px-3 py-2 text-sm text-slate-500">Loading...</p> : null}
              {!customers.isLoading && !(customers.data?.items?.length ?? 0) ? <p className="px-3 py-2 text-sm text-slate-500">No clients found.</p> : null}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 p-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold">Available slots</h3>
              {slots.isFetching ? <span className="text-xs text-slate-500">Refreshing...</span> : null}
            </div>
            {!branchId || !serviceId ? <p className="mt-2 text-sm text-slate-500">Select branch and service to load slots.</p> : null}
            {slots.error ? <p className="mt-2 text-sm text-red-600">{String(slots.error)}</p> : null}
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {(slots.data?.items ?? []).slice(0, 24).map((s) => (
                <button
                  key={`${s.staff_id}-${s.start_at}`}
                  type="button"
                  onClick={() => setSelectedStartAt(s.start_at)}
                  className={cn("rounded-xl border p-2 text-left", selectedStartAt === s.start_at ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white")}
                >
                  <div className="text-sm font-semibold">{new Date(s.start_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })}</div>
                  <div className="truncate text-xs text-slate-500">{activeStaff.find((x) => x.id === s.staff_id)?.full_name || s.staff_id}</div>
                </button>
              ))}
            </div>
            {slots.isLoading ? <div className="skeleton mt-3 h-20 w-full" /> : null}
            {!slots.isLoading && !!branchId && !!serviceId && !(slots.data?.items?.length ?? 0) ? <p className="mt-3 text-sm text-slate-500">No slots available for this day.</p> : null}
          </section>

          <section className="rounded-2xl border border-slate-200 p-4">
            <h3 className="font-semibold">Notes</h3>
            <textarea className="input mt-2 min-h-[88px]" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("manager.calendar.reason.note", "Optional note")} />
          </section>

          <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <h3 className="font-semibold">Summary</h3>
            <div className="mt-2 grid gap-1 text-sm text-slate-700">
              <div>Client: <span className="font-medium">{selectedCustomer?.full_name || "Not selected"}</span></div>
              <div>Service: <span className="font-medium">{selectedService?.name || "Not selected"}</span></div>
              <div>Time: <span className="font-medium">{selectedStartAt ? new Date(selectedStartAt).toLocaleString(locale) : "Not selected"}</span></div>
              <div>Staff: <span className="font-medium">{selectedSlotStaffName || (staffId ? activeStaff.find((x) => x.id === staffId)?.full_name : "Auto") || "Auto"}</span></div>
            </div>
            {inlineErr ? <p className="mt-2 text-sm text-red-600">{inlineErr}</p> : null}
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button type="button" className="btn secondary" onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="btn"
                disabled={create.isPending || !branchId || !serviceId || !customerId || !selectedStartAt}
                onClick={() => void create.mutateAsync()}
              >
                {create.isPending ? "Creating..." : "Create appointment"}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function Modal({ title, subtitle, onClose, children, wide, footer }: { title: string; subtitle?: string; onClose: () => void; children: ReactNode; wide?: boolean; footer?: ReactNode }) {
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-slate-900/45 p-2 sm:items-center sm:p-6" role="dialog" aria-modal="true">
      <button type="button" aria-label="Close modal backdrop" className="absolute inset-0" onClick={onClose} />
      <div className={cn("panel relative z-10 flex max-h-[90vh] w-full flex-col p-4", wide ? "max-w-3xl" : "max-w-lg")}>
        <div className="mb-3 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            {subtitle ? <p className="text-sm text-slate-500">{subtitle}</p> : null}
          </div>
          <button type="button" className="btn secondary" onClick={onClose}>Close</button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer ? <div className="mt-3 shrink-0">{footer}</div> : null}
      </div>
    </div>
  );
}

function ActionDialogModal({
  state, t, pending, onClose, onConfirm,
}: {
  state: Exclude<ActionDialog, { open: false }>; t: any; pending: boolean; onClose: () => void; onConfirm: (reason?: string) => Promise<void>;
}) {
  const [preset, setPreset] = useState("");
  const [note, setNote] = useState("");
  const withReason = state.action === "cancel" || state.action === "no_show";
  const presets = state.action === "cancel" ? ["Client asked", "No response", "Duplicate", "Other"] : ["No arrival", "Late", "Other"];
  const submit = async () => {
    const reason = withReason ? [preset && preset !== "Other" ? preset : "", note].filter(Boolean).join(" - ") || undefined : undefined;
    await onConfirm(reason);
  };
  return (
    <Modal title={t(`manager.calendar.action.${state.action}`, state.action)} subtitle={state.appointment.customer_name || t("manager.calendar.unknownClient", "Unknown client")} onClose={onClose}>
      {withReason ? (
        <div className="grid gap-3">
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => <button key={p} type="button" className={cn("manager-filter-chip", preset === p && "active")} onClick={() => setPreset(p)}>{p}</button>)}
          </div>
          <textarea className="input min-h-[88px]" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("manager.calendar.reason.note", "Optional note")} />
        </div>
      ) : <p className="text-sm text-slate-600">{t("manager.calendar.confirmPrompt", "Confirm this action?")}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className="btn secondary" disabled={pending} onClick={onClose}>{t("manager.calendar.dialog.close", "Close")}</button>
        <button type="button" className="btn" disabled={pending} onClick={() => void submit()}>{pending ? t("manager.calendar.dialog.processing", "Processing...") : t("manager.calendar.dialog.confirm", "Confirm")}</button>
      </div>
    </Modal>
  );
}

function RescheduleModal({
  state, t, locale, staffOptions, pending, onClose, onChangeDay, onChangeStaff, onConfirm,
}: {
  state: Exclude<RescheduleDialog, { open: false }>; t: any; locale: string; staffOptions: Array<{ id: string; label: string }>;
  pending: boolean; onClose: () => void; onChangeDay: (d: string) => void; onChangeStaff: (id: string) => void; onConfirm: (newStartAt: string) => Promise<void>;
}) {
  const [selected, setSelected] = useState("");
  const [inlineErr, setInlineErr] = useState("");
  useEffect(() => { setSelected(""); setInlineErr(""); }, [state.day, state.staff_id]);
  const slots = useQuery({
    queryKey: ["manager-calendar-reschedule-slots", state.appointment.id, state.day, state.staff_id],
    queryFn: () => apiGet<{ items: Slot[] }>(`/v3/calendar/smart-availability?branch_id=${encodeURIComponent(state.appointment.branch_id)}&service_id=${encodeURIComponent(state.appointment.service_id)}&day=${encodeURIComponent(state.day)}${state.staff_id ? `&staff_id=${encodeURIComponent(state.staff_id)}` : ""}${state.appointment.customer_id ? `&customer_id=${encodeURIComponent(state.appointment.customer_id)}` : ""}`),
    enabled: !!state.appointment.branch_id && !!state.appointment.service_id,
  });
  const submit = async () => {
    if (!selected) return;
    try {
      await onConfirm(selected);
    } catch (e) {
      const s = String(e);
      if (s.includes("overlap") || s.includes("conflict")) {
        setInlineErr(t("manager.calendar.reschedule.slotGone", "Slot no longer available"));
        await slots.refetch();
      }
    }
  };
  return (
    <Modal title={t("manager.calendar.action.reschedule", "Reschedule")} subtitle={state.appointment.customer_name || ""} onClose={onClose} wide footer={
      <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
        <button type="button" className="btn secondary" onClick={onClose}>{t("manager.calendar.dialog.close", "Close")}</button>
        <button type="button" className="btn" disabled={pending || !selected} onClick={() => void submit()}>{t("manager.calendar.reschedule.apply", "Apply")}</button>
      </div>
    }>
      <div className="grid gap-3">
        <DateStrip baseDay={state.day} activeDay={state.day} locale={locale} queue onPick={onChangeDay} />
        <div>
          <label className="text-xs text-slate-500">{t("manager.calendar.staff", "Staff")}</label>
          <select className="input mt-1" value={state.staff_id} onChange={(e) => onChangeStaff(e.target.value)}>
            <option value="">{t("manager.calendar.staffAll", "All staff")}</option>
            {staffOptions.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        {inlineErr ? <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{inlineErr}</div> : null}
        {slots.isLoading ? <div className="skeleton h-24 w-full" /> : null}
        {slots.error ? <p className="text-sm text-red-600">{String(slots.error)}</p> : null}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {(slots.data?.items ?? []).map((s) => (
            <button key={`${s.staff_id}-${s.start_at}`} type="button" className={cn("rounded-xl border p-2 text-left", selected === s.start_at ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white")} onClick={() => setSelected(s.start_at)}>
              <div className="text-sm font-semibold">{new Date(s.start_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })}</div>
              <div className="text-xs text-slate-500">{staffOptions.find((x) => x.id === s.staff_id)?.label || s.staff_id}</div>
              {typeof s.score === "number" ? <div className="text-xs text-amber-700">★ {Math.round(s.score)}</div> : null}
            </button>
          ))}
        </div>
        {!slots.isLoading && !(slots.data?.items?.length ?? 0) ? <p className="text-sm text-slate-500">{t("manager.calendar.reschedule.empty", "No slots available for this day.")}</p> : null}
      </div>
    </Modal>
  );
}

function DetailOverlay({
  appointment, appointmentLoading, appointmentError, timeline, timelineLoading, locale, t, onClose, onAction,
}: {
  appointment?: Appointment; appointmentLoading: boolean; appointmentError: unknown; timeline: TimelineItem[]; timelineLoading: boolean; locale: string;
  t: any; onClose: () => void; onAction: (a: Appointment, x: Action) => void;
}) {
  const actions = appointment ? ((appointment.allowed_actions && appointment.allowed_actions.length ? appointment.allowed_actions : allowedActionsForStatus(appointment.status)) as Action[]) : [];
  return (
    <div className="fixed inset-0 z-30 bg-slate-900/35">
      <button type="button" className="absolute inset-0" onClick={onClose} aria-label="Close detail" />
      <div className="absolute inset-y-0 right-0 z-10 w-full max-w-2xl overflow-y-auto border-l border-slate-200 bg-white p-4 shadow-2xl">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">{t("manager.calendar.detail.title", "Appointment detail")}</p>
            <h2 className="text-xl font-semibold">{appointment?.customer_name || (appointmentLoading ? t("loading", "Loading...") : t("manager.calendar.unknownClient", "Unknown client"))}</h2>
          </div>
          <button type="button" className="btn secondary" onClick={onClose}>{t("manager.calendar.detail.close", "Close")}</button>
        </div>
        {appointmentLoading ? <div className="skeleton h-28 w-full" /> : null}
        {appointmentError ? <p className="text-sm text-red-600">{String(appointmentError)}</p> : null}
        {appointment ? (
          <div className="grid gap-4">
            <section className="panel p-4">
              <div className="flex flex-wrap gap-2">
                <StatusPill label={t(`manager.calendar.status.${appointment.status}`, appointment.status)} tone={statusTone(appointment.status) as never} />
                {appointment.customer_risk_tier ? <StatusPill label={t("manager.calendar.riskPill", "risk: {{tier}}", { tier: t(`manager.clients.risk.${appointment.customer_risk_tier}`, appointment.customer_risk_tier) })} tone={riskTone(appointment.customer_risk_tier) as never} /> : null}
              </div>
              <div className="mt-3 grid gap-2 text-sm">
                <div><span className="font-semibold">{t("manager.calendar.detail.schedule", "Schedule")}:</span> {new Date(appointment.start_at).toLocaleString(locale)} - {new Date(appointment.end_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })}</div>
                <div><span className="font-semibold">{t("manager.calendar.detail.service", "Service")}:</span> {appointment.service_name || t("manager.calendar.defaultService", "Service")}</div>
                <div><span className="font-semibold">{t("manager.calendar.detail.staff", "Staff")}:</span> {appointment.staff_name || t("manager.calendar.defaultStaff", "Staff")}</div>
                <div><span className="font-semibold">{t("manager.calendar.detail.branch", "Branch")}:</span> {[appointment.branch_name, appointment.branch_timezone].filter(Boolean).join(" • ")}</div>
                <div><span className="font-semibold">{t("manager.calendar.detail.contact", "Contact")}:</span> {appointment.customer_phone ? <a className="text-sky-700 underline" href={`tel:${appointment.customer_phone}`}>{appointment.customer_phone}</a> : <span className="text-slate-500">{t("manager.calendar.detail.noPhone", "No phone")}</span>}</div>
                {appointment.notes ? <div><span className="font-semibold">{t("manager.calendar.detail.notes", "Notes")}:</span> {appointment.notes}</div> : null}
              </div>
              {actions.length ? <div className="mt-4 flex flex-wrap gap-2">{actions.map((x) => <button key={x} type="button" className="btn secondary" onClick={() => onAction(appointment, x)}>{t(`manager.calendar.action.${x}`, x)}</button>)}</div> : null}
            </section>
            <section className="panel p-4">
              <h3 className="text-base font-semibold">{t("manager.calendar.detail.timeline", "Timeline")}</h3>
              {timelineLoading ? <div className="skeleton mt-3 h-20 w-full" /> : null}
              <div className="mt-3 grid gap-2">
                {timeline.slice(-5).reverse().map((x) => (
                  <div key={x.id} className="rounded-xl border border-slate-200 p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium">{x.event_type}</span>
                      <span className="text-xs text-slate-500">{new Date(x.created_at).toLocaleString(locale)}</span>
                    </div>
                  </div>
                ))}
                {!timelineLoading && !timeline.length ? <p className="text-sm text-slate-500">{t("manager.calendar.detail.timelineEmpty", "No timeline events yet.")}</p> : null}
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
}
